import { useEffect, useState } from "react";
import { Loader2, ExternalLink, Edit3, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";

interface ArticleRow {
  id: string;
  slug: string;
  title: string;
  category: string;
  status: string;
  published: boolean;
  updated_at: string;
}

const openInStudio = (id: string) => {
  // Switch to Content Studio, then open the article in the workspace on Draft tab.
  window.dispatchEvent(new CustomEvent("admin:navigate", { detail: { tab: "contentops" } }));
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent("contentops:open-article", { detail: { id, tab: "draft" } }));
  }, 60);
};

const BlogList = () => {
  const [rows, setRows] = useState<ArticleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | "published" | "drafts">("all");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("blog_articles")
        .select("id,slug,title,category,status,published,updated_at")
        .order("updated_at", { ascending: false })
        .limit(200);
      if (!cancelled) {
        setRows((data as ArticleRow[]) ?? []);
        setLoading(false);
      }
    };
    load();
    const ch = supabase
      .channel("admin-blog-list")
      .on("postgres_changes", { event: "*", schema: "public", table: "blog_articles" }, load)
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(ch); };
  }, []);

  const filtered = rows.filter((r) => {
    if (filter === "published" && !r.published) return false;
    if (filter === "drafts" && r.published) return false;
    if (q && !`${r.title} ${r.slug} ${r.category}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const counts = {
    all: rows.length,
    published: rows.filter((r) => r.published).length,
    drafts: rows.filter((r) => !r.published).length,
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border p-5"
        style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: "hsl(0 0% 95%)" }}>Blog articles</h2>
            <p className="text-xs mt-1" style={{ color: "hsl(0 0% 60%)" }}>
              All writing, SEO, and publishing happens in Content Studio. This list is for quick navigation.
            </p>
          </div>
          <Button size="sm" onClick={() => window.dispatchEvent(new CustomEvent("admin:navigate", { detail: { tab: "contentops" } }))}
            className="h-9 font-semibold" style={{ background: "hsl(207 68% 50%)", color: "white" }}>
            <Plus size={14} className="mr-1.5" /> Open Content Studio
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {(["all", "published", "drafts"] as const).map((f) => {
            const active = filter === f;
            return (
              <button key={f} onClick={() => setFilter(f)}
                className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors"
                style={{
                  background: active ? "hsl(207 68% 50%)" : "hsl(0 0% 9%)",
                  color: active ? "white" : "hsl(0 0% 70%)",
                  border: `1px solid ${active ? "hsl(207 68% 50%)" : "hsl(0 0% 18%)"}`,
                }}>
                {f === "all" ? "All" : f === "published" ? "Published" : "Drafts"}
                <span className="rounded-full px-1.5 py-0.5 text-[10px] tabular-nums"
                  style={{ background: active ? "rgba(255,255,255,0.22)" : "hsl(0 0% 14%)", color: active ? "white" : "hsl(0 0% 60%)" }}>
                  {counts[f]}
                </span>
              </button>
            );
          })}
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, slug, category..."
            className="ml-auto max-w-xs border h-9"
            style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16" style={{ color: "hsl(0 0% 50%)" }}>
          <Loader2 size={18} className="animate-spin mr-2" /> Loading articles...
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border p-12 text-center"
          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <p className="text-sm" style={{ color: "hsl(0 0% 65%)" }}>No articles match.</p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden"
          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          {filtered.map((r) => (
            <div key={r.id}
              className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 transition-colors hover:bg-[hsl(0_0%_8%)]"
              style={{ borderColor: "hsl(0 0% 12%)" }}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium truncate" style={{ color: "hsl(0 0% 92%)" }}>{r.title || "Untitled"}</p>
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    style={{
                      background: r.published ? "hsl(142 70% 35% / 0.18)" : "hsl(0 0% 14%)",
                      color: r.published ? "hsl(142 70% 75%)" : "hsl(0 0% 65%)",
                    }}>
                    {r.published ? "Published" : r.status}
                  </span>
                </div>
                <p className="text-[11px] mt-0.5" style={{ color: "hsl(0 0% 50%)" }}>
                  /{r.slug} · {r.category} · updated {new Date(r.updated_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.published && (
                  <a href={`/blog/${r.slug}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs transition-colors hover:bg-white/5"
                    style={{ borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 80%)" }}>
                    <ExternalLink size={12} /> View
                  </a>
                )}
                <Button size="sm" onClick={() => openInStudio(r.id)}
                  className="h-8 font-medium" style={{ background: "hsl(207 68% 50%)", color: "white" }}>
                  <Edit3 size={12} className="mr-1.5" /> Edit in Studio
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogList;
